import { ChevronDown, Crop, Ungroup } from 'lucide-react';
import { useState } from 'react';

import { IconPicker } from '@/components/canvas/IconPicker';
import { CropModal } from '@/components/canvas/CropModal';
import { findLibraryIcon } from '@/lib/icon-library';
import { CALLOUT_BODY_PLACEHOLDERS, CALLOUT_VARIANT_LABELS, CALLOUT_VARIANTS } from '@/lib/callout';
import { MERMAID_DIAGRAM_TYPES, withDiagramType } from '@/lib/mermaid-types';
import { Input } from '@/components/ui/Input';
import { Section } from '@/components/ui/Section';
import { Select } from '@/components/ui/Select';
import { cn } from '@/lib/utils';
import {
  useActiveBreakpoint,
  useSetBreakpoint,
} from '@/lib/breakpoint-context';
import {
  effectiveElementPosition,
  effectiveElementStyle,
  patchElementPosition,
  patchElementStyle,
} from '@/lib/element-breakpoint-patch';
import type {
  Breakpoint,
  CalloutContent,
  CalloutVariant,
  ChipContent,
  ElementPosition,
  ElementStyle,
  EmbedContent,
  IconContent,
  ImageContent,
  LatexContent,
  McqContent,
  MermaidContent,
  MermaidDiagramType,
  ScriptElement,
  ShapeContent,
  VideoContent,
} from '@/lib/schema.types';
import { useEditorActions, useEditorStore } from '@/store/editor.store';

import { ElementAdvancedProperties } from './ElementAdvancedProperties';

interface Props {
  slideId: string;
  rowId: string;
  columnId: string;
  element: ScriptElement;
}

const BREAKPOINTS: { id: Breakpoint | null; label: string }[] = [
  { id: null, label: 'Desktop' },
  { id: 'tablet', label: 'Tablet' },
  { id: 'mobile', label: 'Mobile' },
];

const FONT_FAMILIES = [
  'Kalam',
  'Chalkduster',
  'Inter',
  'Manrope',
  'Plus Jakarta Sans',
  'Geist',
  'IBM Plex Sans',
  'Space Grotesk',
  'Newsreader',
  'EB Garamond',
];

export function ElementProperties(props: Props) {
  const { slideId, rowId, columnId, element } = props;
  const actions = useEditorActions();
  const mode = useEditorStore((s) => s.mode);
  const isFreeform = useEditorStore(
    (s) =>
      s.script?.slides
        .find((sl) => sl.id === slideId)
        ?.rows.find((r) => r.id === rowId)?.layout === 0,
  );

  // Active editing breakpoint (null = desktop base). Style/position edits are
  // routed to the base or to `responsiveOverrides[bp]` accordingly; reads show
  // the effective (merged) value at that breakpoint.
  const bp = useActiveBreakpoint();
  const setBreakpoint = useSetBreakpoint();
  const effStyle = effectiveElementStyle(element, bp);
  const effPos = effectiveElementPosition(element, bp);

  const setStyle = (patch: Partial<ElementStyle>) =>
    actions.updateElement(slideId, rowId, columnId, element.id, patchElementStyle(element, bp, patch));

  const setContent = (content: ScriptElement['content']) =>
    actions.updateElement(slideId, rowId, columnId, element.id, { content });

  const setPosition = (patch: Partial<ElementPosition>) =>
    actions.updateElement(slideId, rowId, columnId, element.id, patchElementPosition(element, bp, patch));

  const pos = effPos;
  const isTextual = element.type === 'text' || element.type === 'mcq' || element.type === 'chip';
  const fontSize = (effStyle.fontSize as number | undefined) ?? 16;
  const fontWeight = (effStyle.fontWeight as number | undefined) ?? 400;
  const color = (effStyle.color as string | undefined) ?? '#1a1a2e';
  // Display the cascade default that actually paints on the canvas: pack
  // mode uses Kalam (set on .canvas-pack-font in globals.css); scripts
  // inherit Inter. The element only gets an explicit fontFamily written
  // when the teacher picks one (avoids per-element overrides littering the
  // data).
  const cascadeDefault = mode === 'pack' ? 'Kalam' : 'Inter';
  const fontFamily = (effStyle.fontFamily as string | undefined) ?? cascadeDefault;

  return (
    <>
      <Section title="Responsive">
        <div className="inline-flex rounded-md border border-border p-0.5 text-xs">
          {BREAKPOINTS.map((b) => (
            <button
              key={b.label}
              onClick={() => setBreakpoint(b.id)}
              className={cn(
                'rounded px-2.5 py-1 font-medium transition',
                bp === b.id ? 'bg-accent text-white' : 'text-fg-muted hover:text-fg',
              )}
            >
              {b.label}
            </button>
          ))}
        </div>
        {bp && (
          <p className="mt-1.5 text-[10.5px] text-fg-subtle">
            Editing the <strong>{bp}</strong> override — style &amp; position changes apply only
            at this breakpoint (desktop stays the base).
          </p>
        )}
      </Section>

      {element.groupId && (
        <Section title="Group">
          <button
            onClick={() =>
              actions.ungroupFreeformElements(slideId, rowId, columnId, element.groupId!)
            }
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-[13px] text-fg hover:bg-bg-subtle"
          >
            <Ungroup size={13} /> Ungroup
          </button>
        </Section>
      )}

      {isTextual && (
        <Section title="Typography">
          <FieldLabel>Font Family</FieldLabel>
          <div className="relative">
            <select
              value={fontFamily}
              onChange={(e) => setStyle({ fontFamily: e.target.value })}
              className="h-9 w-full appearance-none rounded-md border border-border bg-white px-3 pr-8 text-sm text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
            >
              {FONT_FAMILIES.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
            <ChevronDown
              size={14}
              className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-fg-subtle"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <FieldLabel>Size</FieldLabel>
              <SuffixInput
                value={fontSize}
                suffix="px"
                onChange={(v) => setStyle({ fontSize: Number(v) || 16 })}
              />
            </div>
            <div>
              <FieldLabel>Weight</FieldLabel>
              <Select
                value={String(fontWeight)}
                onChange={(e) => setStyle({ fontWeight: Number(e.target.value) })}
              >
                <option value="300">Light</option>
                <option value="400">Regular</option>
                <option value="500">Medium</option>
                <option value="600">Semibold</option>
                <option value="700">Bold</option>
                <option value="800">Extra Bold</option>
              </Select>
            </div>
          </div>

          <div>
            <FieldLabel>Color</FieldLabel>
            <ColorRow value={color} onChange={(v) => setStyle({ color: v })} />
          </div>

          <div>
            <FieldLabel>Align</FieldLabel>
            <Select
              value={(effStyle.textAlign as string | undefined) ?? 'left'}
              onChange={(e) =>
                setStyle({ textAlign: e.target.value as 'left' | 'center' | 'right' })
              }
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </Select>
          </div>
        </Section>
      )}

      {element.type === 'text' && (
        <Section title="Writing animation">
          <label className="flex cursor-pointer items-center justify-between gap-3">
            <span className="text-[13px] text-fg">
              Handwriting reveal
              <span className="mt-0.5 block text-[10.5px] text-fg-subtle">
                Plays the pen-writing animation in the classroom app. Off → the
                text just appears.
              </span>
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={!!element.writingAnimation}
              onClick={() =>
                actions.updateElement(slideId, rowId, columnId, element.id, {
                  writingAnimation: !element.writingAnimation,
                })
              }
              className={cn(
                'relative h-5 w-9 shrink-0 rounded-full transition-colors',
                element.writingAnimation ? 'bg-accent' : 'bg-border',
              )}
            >
              <span
                className={cn(
                  'absolute top-0.5 block h-4 w-4 rounded-full bg-white shadow-sm transition-transform',
                  element.writingAnimation ? 'translate-x-4' : 'translate-x-0.5',
                )}
              />
            </button>
          </label>
        </Section>
      )}

      {isFreeform && (
        <Section title="Position">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <FieldLabel>X</FieldLabel>
              <SuffixInput
                value={pos?.x ?? 0}
                suffix="px"
                onChange={(v) => setPosition({ x: Number(v) || 0 })}
              />
            </div>
            <div>
              <FieldLabel>Y</FieldLabel>
              <SuffixInput
                value={pos?.y ?? 0}
                suffix="px"
                onChange={(v) => setPosition({ y: Number(v) || 0 })}
              />
            </div>
            <div>
              <FieldLabel>Width</FieldLabel>
              <SuffixInput
                value={pos?.w ?? 240}
                suffix="px"
                onChange={(v) => setPosition({ w: Math.max(20, Number(v) || 20) })}
              />
            </div>
            <div>
              <FieldLabel>Height</FieldLabel>
              <SuffixInput
                value={pos?.h ?? 120}
                suffix="px"
                onChange={(v) => setPosition({ h: Math.max(20, Number(v) || 20) })}
              />
            </div>
            <div>
              <FieldLabel>Rotation</FieldLabel>
              <SuffixInput
                value={pos?.rotation ?? 0}
                suffix="°"
                onChange={(v) => setPosition({ rotation: Number(v) || 0 })}
              />
            </div>
            <div>
              <FieldLabel>Z-index</FieldLabel>
              <SuffixInput
                value={pos?.zIndex ?? 1}
                suffix=""
                onChange={(v) => setPosition({ zIndex: Number(v) || 1 })}
              />
            </div>
          </div>
        </Section>
      )}

      <Section title="Content">
        <ContentEditor element={element} slideId={slideId} setContent={setContent} />
      </Section>

      <ElementAdvancedProperties style={effStyle} onStyle={setStyle} />

      <Section title="Identifier">
        <div className="rounded-md bg-bg-subtle px-2.5 py-1.5 font-mono text-[11px] text-fg-muted">
          {element.id}
        </div>
      </Section>
    </>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1 block text-[11px] font-medium text-fg-muted">{children}</span>
  );
}

function SuffixInput({
  value,
  suffix,
  onChange,
}: {
  value: number | string;
  suffix: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex h-9 items-center rounded-md border border-border bg-white pl-3 pr-2 focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20">
      <input
        value={value}
        type="number"
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-transparent text-sm text-fg outline-none"
      />
      <span className="text-xs text-fg-subtle">{suffix}</span>
    </div>
  );
}

function ColorRow({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex h-9 items-center gap-2 rounded-md border border-border bg-white pl-2 pr-3 focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-6 w-7 cursor-pointer rounded-sm border border-border bg-transparent"
        aria-label="Pick color"
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-transparent text-sm text-fg outline-none"
      />
      <span className="text-xs text-fg-subtle">100%</span>
    </div>
  );
}

function ContentEditor({
  element,
  slideId,
  setContent,
}: {
  element: ScriptElement;
  slideId: string;
  setContent: (c: ScriptElement['content']) => void;
}) {
  switch (element.type) {
    case 'text': {
      const text = typeof element.content === 'string' ? element.content : '';
      return (
        <textarea
          value={text}
          onChange={(e) => setContent(e.target.value)}
          rows={4}
          className="w-full rounded-md border border-border bg-white p-2 text-sm text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
        />
      );
    }
    case 'image':
      return <ImageContentEditor element={element} setContent={setContent} />;
    case 'video': {
      const c = (element.content as VideoContent | undefined) ?? { src: '' };
      return (
        <div className="space-y-2">
          <Input
            label="Video URL (mp4)"
            value={c.src}
            onChange={(e) => setContent({ ...c, src: e.target.value })}
          />
          <Input
            label="Poster URL"
            value={c.poster ?? ''}
            onChange={(e) => setContent({ ...c, poster: e.target.value })}
          />
        </div>
      );
    }
    case 'embed': {
      const c = (element.content as EmbedContent | undefined) ?? { url: '' };
      return (
        <div className="space-y-2">
          <Input
            label="URL"
            value={c.url}
            onChange={(e) => setContent({ ...c, url: e.target.value })}
          />
          <Input
            label="Height (px)"
            type="number"
            value={c.height ?? 320}
            onChange={(e) => setContent({ ...c, height: Number(e.target.value) || 320 })}
          />
        </div>
      );
    }
    case 'chip': {
      const c = (element.content as ChipContent | undefined) ?? { label: '' };
      return (
        <Input
          label="Label"
          value={c.label}
          onChange={(e) => setContent({ ...c, label: e.target.value })}
        />
      );
    }
    case 'shape': {
      const c = (element.content as ShapeContent | undefined) ?? { shape: 'rect' };
      return (
        <Select
          label="Shape"
          value={c.shape}
          onChange={(e) => setContent({ ...c, shape: e.target.value as ShapeContent['shape'] })}
        >
          <option value="rect">Rectangle</option>
          <option value="circle">Circle</option>
          <option value="ellipse">Ellipse</option>
          <option value="triangle">Triangle</option>
          <option value="diamond">Diamond</option>
          <option value="pentagon">Pentagon</option>
          <option value="hexagon">Hexagon</option>
          <option value="star">Star</option>
          <option value="arrow">Arrow</option>
          <option value="heart">Heart</option>
          <option value="cross">Cross</option>
          <option value="line">Line</option>
        </Select>
      );
    }
    case 'mcq':
      // mcq editing must, on a pack-mode quiz slide, also sync beatMeta so
      // element.content + beatMeta stay consistent (SCHEMA_USAGE.md "Quiz
      // consistency rules"). McqEditor batches both ops in one PATCH via
      // the existing debounced flush.
      return <McqEditor element={element} slideId={slideId} setContent={setContent} />;
    case 'latex': {
      const c = (element.content as LatexContent | undefined) ?? { source: '' };
      return (
        <div className="space-y-2">
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium text-fg-muted">
              TeX source
            </span>
            <textarea
              value={c.source}
              onChange={(e) => setContent({ ...c, source: e.target.value })}
              rows={4}
              spellCheck={false}
              className="block w-full resize-y rounded-md border border-border bg-white px-2 py-1.5 font-mono text-xs text-fg outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
          </label>
          <Select
            label="Display"
            value={c.displayMode ? 'block' : 'inline'}
            onChange={(e) => setContent({ ...c, displayMode: e.target.value === 'block' })}
          >
            <option value="block">Block (centered)</option>
            <option value="inline">Inline</option>
          </Select>
        </div>
      );
    }
    case 'mermaid': {
      const c = (element.content as MermaidContent | undefined) ?? {
        source: '',
        diagramType: 'flowchart-td' as MermaidDiagramType,
      };
      return (
        <div className="space-y-2">
          <Select
            label="Diagram type"
            value={c.diagramType}
            onChange={(e) => {
              const t = e.target.value as MermaidDiagramType;
              setContent({ ...c, diagramType: t, source: withDiagramType(c.source, t) });
            }}
          >
            {MERMAID_DIAGRAM_TYPES.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </Select>
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium text-fg-muted">Source</span>
            <textarea
              value={c.source}
              onChange={(e) => setContent({ ...c, source: e.target.value })}
              rows={8}
              spellCheck={false}
              className="block w-full resize-y rounded-md border border-border bg-white px-2 py-1.5 font-mono text-xs text-fg outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
          </label>
          <Input
            label="Caption (optional)"
            value={c.caption ?? ''}
            onChange={(e) => setContent({ ...c, caption: e.target.value })}
          />
          <p className="text-[10.5px] text-fg-subtle">
            Live preview renders on the slide. Changing the diagram type rewrites the first line.
          </p>
        </div>
      );
    }
    case 'icon':
      return <IconContentEditor element={element} setContent={setContent} />;
    case 'callout': {
      const c = (element.content as CalloutContent | undefined) ?? {
        variant: 'important_concept',
        body: '',
      };
      return (
        <div className="space-y-2">
          <Select
            label="Variant"
            value={c.variant}
            onChange={(e) =>
              setContent({ ...c, variant: e.target.value as CalloutVariant })
            }
          >
            {CALLOUT_VARIANTS.map((v) => (
              <option key={v} value={v}>
                {CALLOUT_VARIANT_LABELS[v]}
              </option>
            ))}
          </Select>
          <Input
            label="Title (optional)"
            value={c.title ?? ''}
            onChange={(e) => setContent({ ...c, title: e.target.value })}
          />
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium text-fg-muted">Body</span>
            <textarea
              value={c.body}
              onChange={(e) => setContent({ ...c, body: e.target.value })}
              rows={3}
              placeholder={CALLOUT_BODY_PLACEHOLDERS[c.variant]}
              className="block w-full resize-y rounded-md border border-border bg-white px-2 py-1.5 text-sm text-fg outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
          </label>
        </div>
      );
    }
    default:
      return <div className="text-xs text-fg-subtle">No editor available.</div>;
  }
}

function ImageContentEditor({
  element,
  setContent,
}: {
  element: ScriptElement;
  setContent: (c: ScriptElement['content']) => void;
}) {
  const c = (element.content as ImageContent | undefined) ?? { src: '' };
  const [cropOpen, setCropOpen] = useState(false);
  return (
    <div className="space-y-2">
      <Input
        label="Image URL"
        value={c.src}
        onChange={(e) => setContent({ ...c, src: e.target.value })}
      />
      <Input
        label="Alt text"
        value={c.alt ?? ''}
        onChange={(e) => setContent({ ...c, alt: e.target.value })}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!c.src}
          onClick={() => setCropOpen(true)}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-[13px] text-fg hover:bg-bg-subtle disabled:opacity-50"
        >
          <Crop size={13} /> {c.crop ? 'Edit crop' : 'Crop'}
        </button>
        {c.crop && (
          <button
            type="button"
            onClick={() => setContent({ ...c, crop: undefined })}
            className="text-[12px] text-fg-subtle hover:text-fg"
          >
            Remove crop
          </button>
        )}
      </div>
      {c.src && (
        <CropModal
          open={cropOpen}
          onClose={() => setCropOpen(false)}
          src={c.src}
          value={c.crop}
          onApply={(crop) => {
            setContent({ ...c, crop });
            setCropOpen(false);
          }}
        />
      )}
    </div>
  );
}

function IconContentEditor({
  element,
  setContent,
}: {
  element: ScriptElement;
  setContent: (c: ScriptElement['content']) => void;
}) {
  const c = (element.content as IconContent | undefined) ?? { id: 'idea' };
  const [pickerOpen, setPickerOpen] = useState(false);
  const { Glyph, name } = findLibraryIcon(c.id);
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        className="flex w-full items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-left text-sm hover:bg-bg-subtle"
      >
        <Glyph size={20} />
        <span className="flex-1">{name}</span>
        <span className="text-[11px] text-fg-subtle">Change…</span>
      </button>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium text-fg-muted">Size (px)</span>
          <input
            type="number"
            min={12}
            value={c.size ?? 64}
            onChange={(e) => setContent({ ...c, size: Number(e.target.value) || 64 })}
            className="h-9 w-full rounded-md border border-border bg-white px-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
        </label>
      </div>
      <Input
        label="Caption (optional)"
        value={c.caption ?? ''}
        onChange={(e) => setContent({ ...c, caption: e.target.value })}
      />
      <IconPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        value={c.id}
        onPick={(id) => setContent({ ...c, id })}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* MCQ editor — script-shape edits + pack-mode beatMeta sync                  */
/* -------------------------------------------------------------------------- */

function McqEditor({
  element,
  slideId,
  setContent,
}: {
  element: ScriptElement;
  slideId: string;
  setContent: (c: ScriptElement['content']) => void;
}) {
  const actions = useEditorActions();
  // Look up the slide so we know whether this is a pack-mode quiz (need
  // beatMeta sync) or a plain script-mode mcq (existing behavior only).
  const slide = useEditorStore((s) => s.script?.slides.find((sl) => sl.id === slideId));
  const mode = useEditorStore((s) => s.mode);
  const isQuizSlide = mode === 'pack' && slide?.beatMeta?.kind === 'quiz';

  const c =
    (element.content as McqContent | undefined) ?? {
      question: '',
      options: ['', '', '', ''],
      answerIndex: 0,
    };

  // Backfill optionIds when absent (legacy mcqs, freshly-promoted slides).
  // Positional ids ('a','b','c',…) match the convention createQuizSlide uses.
  const optionIds: string[] =
    c.optionIds && c.optionIds.length === c.options.length
      ? c.optionIds
      : c.options.map((_, i) => String.fromCharCode('a'.charCodeAt(0) + i));
  const correctOptionId = optionIds[c.answerIndex] ?? optionIds[0] ?? 'a';

  /** Helpers — apply an edit + (if quiz) sync beatMeta in the same tick. */
  const writeContent = (next: McqContent) => setContent(next);

  const setQuestion = (q: string) =>
    writeContent({ ...c, optionIds, correctOptionId, question: q });

  const setOpt = (i: number, value: string) => {
    const options = [...c.options];
    options[i] = value;
    // optionIds + correctOptionId carry over; branches keys unchanged.
    writeContent({ ...c, options, optionIds, correctOptionId });
  };

  const setAnswer = (newIndex: number) => {
    const newCorrectId = optionIds[newIndex] ?? optionIds[0];
    writeContent({
      ...c,
      answerIndex: newIndex,
      optionIds,
      correctOptionId: newCorrectId,
    });
    if (!isQuizSlide || !slide?.beatMeta) return;

    // Quiz consistency: keep branches keyed by WRONG option ids only.
    // The new correct option drops its branch; the old one (now wrong)
    // gains an empty branch placeholder so it's ready for reteach
    // authoring.
    const oldBranches =
      (slide.beatMeta.branches as Record<string, Record<string, unknown>> | undefined) ?? {};
    const nextBranches: Record<string, Record<string, unknown>> = {};
    for (const id of optionIds) {
      if (id === newCorrectId) continue; // never branch on the correct answer
      nextBranches[id] = oldBranches[id] ?? {
        misconceptionId: null,
        diagnosis: '',
        reteachLevel1: [],
        reteachLevel2: [],
      };
    }
    actions.updateBeatMeta(slideId, {
      correctOptionId: newCorrectId,
      branches: nextBranches,
    });
  };

  return (
    <div className="space-y-2">
      <Input
        label="Question"
        value={c.question}
        onChange={(e) => setQuestion(e.target.value)}
      />
      <div className="space-y-1.5">
        {c.options.map((opt, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="radio"
              checked={c.answerIndex === i}
              onChange={() => setAnswer(i)}
              title="Mark as the correct answer"
            />
            <span className="grid h-5 w-5 place-items-center rounded bg-bg-subtle text-[10px] font-semibold text-fg-muted">
              {optionIds[i] ?? '?'}
            </span>
            <input
              value={opt}
              onChange={(e) => setOpt(i, e.target.value)}
              placeholder={`Option ${String.fromCharCode(65 + i)}`}
              className="h-8 flex-1 rounded-md border border-border bg-white px-2 text-xs text-fg"
            />
          </div>
        ))}
      </div>
      {isQuizSlide && (
        <p className="text-[10.5px] text-fg-subtle">
          The correct answer and its reteach branches update automatically when
          you edit the options.
        </p>
      )}
    </div>
  );
}
